import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { Customer } from '@typesApp/Customer'
import { connectorActionModuleSelector } from '@redux/slices/connectorSlice'
import { CustomerChangedError, getErrorMessage, InvalidCustomerError } from '@utils/errorUtil'
import { isCustomerNumberChanged, isCustomerNumberValid } from '@utils/isCustomerNumberValid'
import { sanitizeCustomer } from '@utils/sanitizeCustomer'
import { useEnvironment } from './useEnvironment'

type UseCustomerReturn = {
  /**
   * @returns sanitized customer object containing the current commerce customer details
   * @throws InvalidCustomerError on error
   * @throws Error if getCustomer hook is missing
   */
  getCustomer: () => Promise<Customer>

  /**
   * Fetches latest commerce customer and validates against current customer number
   * @param customerNumber current customer number if available
   * @throws InvalidCustomerError if commerce returns error or if customer id is not valid
   * @throws CustomerChangedError if commerce customer has a different customer number
   * @returns latest customer object
   */
  validateCustomerOrThrow: (customerNumber?: string) => Promise<Customer>
}

export const useCustomer = (): UseCustomerReturn => {
  const { anonymousUserId } = useEnvironment()
  const actionModule = useSelector(connectorActionModuleSelector)

  const getCustomer = useCallback(async () => {
    if (typeof actionModule?.getCustomer !== 'function') {
      throw new Error('Invalid configuration, getCustomer hook missing')
    }

    let customer: Customer | undefined
    try {
      customer = await actionModule.getCustomer()
    } catch (error) {
      // commerce may throw an error if customer session has expired
      throw new InvalidCustomerError(getErrorMessage(error))
    }

    return sanitizeCustomer(customer)
  }, [actionModule])

  const validateCustomerOrThrow = useCallback(
    async (customerNumber?: string) => {
      const customer = await getCustomer()

      if (!customer?.id || !isCustomerNumberValid(customer?.id, anonymousUserId)) {
        throw new InvalidCustomerError('Got invalid customer from commerce')
      }

      if (customerNumber && isCustomerNumberChanged(customer.id, customerNumber)) {
        throw new CustomerChangedError('Customer has changed')
      }

      return customer
    },
    [getCustomer, anonymousUserId]
  )

  return {
    getCustomer,
    validateCustomerOrThrow,
  }
}
